import React, { useContext, useMemo } from 'react'
import { Typography, Grid } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { BOOKING_PRE_PAYMENT, BOOKING_STATUS, getCurrency, MAXIMUM_APEPAGO_LOAN_AMOUNT } from '../../constants'
import { Context } from '../../Store'
import { isBookingR2R } from '../booking'
import { useFormContext } from 'react-hook-form'
import { BookingContext } from '../BookingContext'
import { formatToItalianCurrency } from '../formatToItalianCurrency'

export const getPricing = (property, booking, hasCounteroffer) => {
  const { consumptionCosts = 0 } = property
  const { extra_fee, prop } = booking
  const isBookingPending = booking.status === BOOKING_STATUS.PENDING
  const price = hasCounteroffer ? booking.counter_offer : isBookingPending ? booking.tenant_proposal : property.price
  const monthly = hasCounteroffer
    ? booking.counter_offer
    : parseFloat(prop?.tari || 0) + parseFloat(price) + parseFloat(consumptionCosts || 0)
  const monthlyMinusPrePayment = monthly - BOOKING_PRE_PAYMENT
  const upfrontFee = extra_fee

  const deposit = isBookingR2R(booking)
    ? price * booking.deposit
    : (price - (booking?.property_total_cost || 0) - (booking?.monthly_fee || 0)) * booking.deposit
  return { price, monthly, monthlyMinusPrePayment, upfrontFee, deposit }
}

const PriceFlowContent = ({
  total,
  propertyCurrency,
  totalMinusPrePayment,
  zappyrentComission,
  hasPrePayment,
  flexibilityStatus,
  depositAmount,
}) => {
  const fontVariant = { fontSize: '1rem' }
  const { t } = useTranslation()
  const ZAPPYRENT_COMMISSION = (zappyrentComission / total).toFixed(1)
  const calculateAmountForBNPL = (type) => {
    let amount = 0;
  
    const remainingLoan = Math.max(MAXIMUM_APEPAGO_LOAN_AMOUNT - zappyrentComission, 0);
  
    if (type === 'zapprent_commision') {
      amount = Math.max(zappyrentComission - MAXIMUM_APEPAGO_LOAN_AMOUNT, 0);
    } else if (type === 'deposit_amount') {
      amount = Math.max(depositAmount - remainingLoan, 0);
    }
  
    return formatToItalianCurrency(amount);
  };
  return (
    <Grid container
      item
      direction="row"
      spacing={2}
      lg={10}
      xs={12}
    >
      <Grid
        style={{
          width: '100%',
          backgroundColor: '#F7FEFF',
          borderRadius: '0.625rem',
          border: '0.0625rem solid #DCF2F4',
          padding: '1rem',
        }}
      >
        <Grid item xs={12}>
          <div
            className="flex flex-row items-start justify-between"
            style={{
              paddingBottom: '1.25rem',
              borderBottom: '1px',
              borderStyle: 'dashed',
              borderColor: '#E5E5E5',
              marginBottom: '1rem',
            }}
          >
            <Typography style={{ fontWeight: 700, fontSize: '0.875rem', color: '#394555' }}>
              {t('propertyDetail.price.totalRentAmount')}
            </Typography>
            <Typography
              style={{ fontWeight: 700, minWidth: '3.125rem', fontSize: '0.875rem', color: '#394555', textAlign: 'right' }}
            >
              {total} {propertyCurrency}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className="flex flex-row items-start justify-between">
            <Typography style={{ fontWeight: 700, ...fontVariant }}>{t('propertyDetail.price.payNow')}:</Typography>
            <Typography style={{ fontWeight: 700, minWidth: '3.125rem', textAlign: 'right', ...fontVariant }}>
              {hasPrePayment ? BOOKING_PRE_PAYMENT : 0} {propertyCurrency}
            </Typography>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className="flex flex-row items-start justify-between">
            <div>
              <Typography style={{ fontWeight: 700, marginBottom: 0, ...fontVariant }}>
                {t('propertyDetail.price.requestIsAccepted')}:
              </Typography>
              <Typography style={{ fontWeight: 400, fontSize: '0.75rem', color: '#757575' }}>
                {hasPrePayment
                  ? t('propertyDetail.price.requestIsAcceptedSubInfoWithPrePayment', {
                    amount: total,
                    pre_payment: BOOKING_PRE_PAYMENT,
                  })
                  : t('propertyDetail.price.requestIsAcceptedSubInfoWithoutPrePayment', { amount: total })}
              </Typography>
            </div>
            <Typography style={{ fontWeight: 700, minWidth: '3.125rem', textAlign: 'right', ...fontVariant }}>
              {hasPrePayment ? totalMinusPrePayment : total} {propertyCurrency}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className="flex flex-row items-start justify-between">
            <div>
              <Typography style={{ fontWeight: 700, marginBottom: 0, ...fontVariant }}>
                {t('propertyDetail.price.creditCheckApproval')}:
              </Typography>
              <Typography style={{ fontWeight: 400, fontSize: '0.75rem', color: '#757575' }}>
                {t('propertyDetail.price.creditCheckApprovalSubInfo', { commission: ZAPPYRENT_COMMISSION })}
              </Typography>
            </div>
            <Typography style={{ fontWeight: 700, minWidth: '3.125rem', textAlign: 'right', ...fontVariant }}>
              {flexibilityStatus ? calculateAmountForBNPL('zapprent_commision'): zappyrentComission} {propertyCurrency}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className="flex flex-row items-start justify-between">
            <div>
              <Typography style={{ fontWeight: 700, marginBottom: 0, ...fontVariant }}>
                {t('propertyDetail.price.contractSigned')}:
              </Typography>
              <Typography style={{ fontWeight: 400, fontSize: '0.75rem', color: '#757575' }}>
                {t('propertyDetail.price.contractSignedSubInfo')}
              </Typography>
            </div>
            <Typography style={{ fontWeight: 700, minWidth: '3.125rem', textAlign: 'right', ...fontVariant }}>
              {flexibilityStatus ? calculateAmountForBNPL('deposit_amount'): depositAmount} {propertyCurrency}
            </Typography>
          </div>
        </Grid>
      </Grid>
    </Grid>
  )
}

const PricingCard = () => {
  const [state] = useContext(Context)
  const { hasCounteroffer } = useContext(BookingContext)
  const { watch } = useFormContext()
  const flexibilityStatus = watch('flexibility_status')
  const { currency = 'EUR' } = state.property
  const {
    monthly: total,
    monthlyMinusPrePayment: totalMinusPrePayment,
    upfrontFee: zappyrentComission,
    deposit: depositAmount,
  } = useMemo(() => getPricing(state.property, state.booking, hasCounteroffer), [state.property, state.booking, hasCounteroffer])
  const { value: propertyCurrency } = getCurrency(currency)
  return (
    <PriceFlowContent
      total={total}
      propertyCurrency={propertyCurrency}
      totalMinusPrePayment={totalMinusPrePayment}
      zappyrentComission={zappyrentComission}
      hasPrePayment={state.booking.has_pre_payment}
      flexibilityStatus={flexibilityStatus}
      depositAmount={depositAmount}
    />
  )
}

export default PricingCard
